import React from "react";
import { ConfigProvider, theme as antdTheme } from "antd";
import { useDashTheme } from "../hooks/useDashTheme";

/**
 * DashAntd — antd, wearing the DashboardV2 palette.
 *
 * The app already mounts a global ConfigProvider, but its tokens describe the
 * LEGACY surface: `colorPrimary #3b82f6`, `colorBgBase #ffffff`,
 * `colorBorder #f0f0f0`. DashboardV2 runs a different palette entirely —
 * a deeper canvas (#E9ECF1) with white cards lifting off it, primary #4273D1,
 * border #DCE0E7.
 *
 * The visible consequence: an antd Segmented derives its track from
 * `colorBgBase`, so on the dashboard it rendered as a white pill on a grey
 * canvas with no edge and no relationship to the controls beside it — the
 * toolbar read as three unrelated floating shapes rather than one row.
 *
 * ConfigProviders nest and merge, so this overrides only its own subtree.
 * Everything outside DashboardV2 keeps the legacy tokens untouched.
 *
 * Mount it around any DashboardV2 region that uses antd. The drill-down drawer
 * needs its own copy because it renders through a portal to document.body and
 * therefore sits outside every in-tree provider.
 */
export function DashAntd({ children }) {
  const t = useDashTheme();

  const cfg = React.useMemo(() => ({
    algorithm: t.isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: t.c.primary,
      colorInfo: t.c.primary,
      colorSuccess: t.status.positive,
      colorWarning: t.status.warning,
      colorError: t.status.negative,

      // Surfaces — the three-level system (canvas / well / card) the dashboard
      // palette is built on. Getting these right is what makes an antd control
      // sit ON the dashboard instead of floating above it.
      colorBgBase: t.c.surface,
      colorBgContainer: t.c.surface,
      colorBgElevated: t.c.surface,
      colorBgLayout: t.c.bg,
      colorFillSecondary: t.c.surfaceMuted,
      colorFillTertiary: t.c.surfaceMuted,
      colorFillQuaternary: t.c.surfaceHover,

      colorBorder: t.c.border,
      colorBorderSecondary: t.c.border,

      colorTextBase: t.c.text,
      colorText: t.c.text,
      colorTextSecondary: t.c.textSecondary,
      colorTextTertiary: t.c.textTertiary,
      colorTextQuaternary: t.c.textTertiary,

      fontFamily: t.font.family,
      fontSize: t.font.size.base,
      borderRadius: t.r.md,
      borderRadiusLG: t.r.lg,
      borderRadiusSM: t.r.sm,
      // One control height across the toolbar, so the segmented control, the
      // buttons and the picker line up on a single baseline.
      controlHeight: 32,
      controlHeightSM: 28,
      boxShadow: t.shadow.sm,
      boxShadowSecondary: t.shadow.md,
      wireframe: false,
    },
    components: {
      Segmented: {
        /* Selection is carried by a TINT, not by a raised grey thumb.
           antd's default (grey track, white thumb) collapses on this palette:
           inside the toolbar card the thumb is #FFFFFF on a #F1F3F7 track —
           1.11 contrast — and in dark mode the thumb (#252728) is IDENTICAL to
           the toolbar background, so the selected item dissolves into it.
           Measured both before changing this. The tint is the dashboard's own
           selection idiom and is unambiguous in either theme. */
        trackBg: "transparent",
        itemSelectedBg: t.c.primarySoft,
        /* Measured on the tint, not on the canvas: #4273D1 on #E9F1FE is 4.01,
           under WCAG AA's 4.5 for body text. The darker primary clears it at
           5.36. Dark mode's #789FE8 on #131F33 is already 6.23. */
        itemSelectedColor: t.isDark ? t.c.primary : t.c.primaryHover,
        itemColor: t.c.textSecondary,
        itemHoverBg: t.c.surfaceHover,
        itemHoverColor: t.c.text,
        trackPadding: 0,
        borderRadius: t.r.sm,
        borderRadiusSM: t.r.sm,
      },
      Button: {
        defaultBg: t.c.surface,
        defaultBorderColor: t.c.border,
        defaultColor: t.c.textSecondary,
        defaultHoverBg: t.c.surfaceHover,
        defaultHoverColor: t.c.text,
        defaultHoverBorderColor: t.c.borderStrong,
        primaryShadow: "none",
        defaultShadow: "none",
      },
      Card: {
        colorBgContainer: t.c.surface,
        headerBg: "transparent",
        headerFontSize: t.font.size.base,
        borderRadiusLG: t.r.lg,
      },
      Tag: { borderRadiusSM: t.r.xs, defaultBg: t.c.surfaceMuted, defaultColor: t.c.textSecondary },
      Tooltip: { colorBgSpotlight: t.isDark ? t.c.surfaceHover : "#1D1F21", borderRadius: t.r.md },
      DatePicker: {
        cellActiveWithRangeBg: t.c.primarySoft,
        activeBorderColor: t.c.primary,
        hoverBorderColor: t.c.borderStrong,
        colorBgElevated: t.c.surface,
      },
      Alert: { borderRadiusLG: t.r.md, withDescriptionPadding: "10px 14px" },
      Empty: { colorTextDescription: t.c.textTertiary },
      Statistic: { contentFontSize: 22, titleFontSize: 12 },
      Skeleton: {
        // antd's own shimmer, matched to the canvas the same way OrbitSkeleton
        // is — its defaults are tuned for a white page.
        gradientFromColor: t.isDark ? "rgba(255,255,255,0.07)" : "rgba(17,24,39,0.08)",
        gradientToColor: t.isDark ? "rgba(255,255,255,0.17)" : "rgba(17,24,39,0.20)",
      },
    },
  }), [t]);

  return <ConfigProvider theme={cfg}>{children}</ConfigProvider>;
}

export default DashAntd;
